import { useEffect, useMemo, useState } from 'react';

import { useAuth, useApiClient } from '../../providers/auth-provider';
import { InfoHint } from '../../shell/InfoHint';

import { ScopePills } from './ScopePills';

import type {
  ApiKeyAudience,
  ApiKeyIdentityMode,
  CreateApiKeyBody,
  CreateApiKeyResponse,
} from '../../hooks/use-api-keys';
import type { AgentSummaryResponse, MySkill, RoleResponse } from '../../lib/api-client';

const DEFAULTS: Record<ApiKeyAudience, string[]> = {
  mcp: ['mcp:read', 'mcp:execute'],
  direct_api: [],
  openai_completions: ['completions:execute'],
  integration: ['integration:provision', 'conversations:read', 'conversations:write'],
};

const AUDIENCE_OPTIONS: { value: ApiKeyAudience; label: string; hint: string }[] = [
  { value: 'mcp', label: 'MCP', hint: 'For LLM clients (Claude Desktop, agents) via MCP.' },
  {
    value: 'openai_completions',
    label: 'OpenAI Completions',
    hint: 'OpenAI-compatible /chat/completions — e.g. VAPI voice.',
  },
  { value: 'direct_api', label: 'Direct API', hint: 'For scripts and backends via REST.' },
];

interface MintKeyModalProps {
  /** Initial audience; the modal lets the user switch it (audience-first). */
  audience: ApiKeyAudience;
  onClose: () => void;
  onMinted: (result: CreateApiKeyResponse) => void;
  createKey: (body: CreateApiKeyBody) => Promise<CreateApiKeyResponse>;
}

export function MintKeyModal({ audience: initialAudience, onClose, onMinted, createKey }: MintKeyModalProps) {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  // "Admin" for impersonation purposes — mirrors the server gate.
  const isAdmin = !!(caps.full_access || caps.manage_users || caps.manage_agents);

  const [audience, setAudience] = useState<ApiKeyAudience>(initialAudience);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([...DEFAULTS[initialAudience]]);
  const [identityMode, setIdentityMode] = useState<ApiKeyIdentityMode>('self');
  const [agentId, setAgentId] = useState('');
  const [roleId, setRoleId] = useState<string>(user?.role_id ?? '');
  const [narrowSkills, setNarrowSkills] = useState(false);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  // OpenAI-completions sub-options (extensible; more added over time).
  // vapiMode gates the VAPI-specific fields (assistant id + session mapping). Off by
  // default → a plain, stateless OpenAI-compatible key (LangChain, OpenAI SDK, backend).
  const [vapiMode, setVapiMode] = useState(false);
  const [vapiSessionMapping, setVapiSessionMapping] = useState(false);
  const [assistantId, setAssistantId] = useState('');
  const [customAuthKey, setCustomAuthKey] = useState('');
  const [fillerEnabled, setFillerEnabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Lazy-loaded option lists (only when an admin opens the relevant picker).
  const [agents, setAgents] = useState<AgentSummaryResponse[]>([]);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [mySkills, setMySkills] = useState<MySkill[]>([]);

  // Reset scopes to the audience default whenever the audience changes.
  useEffect(() => {
    setScopes([...DEFAULTS[audience]]);
  }, [audience]);

  // Load agents/roles for admins; load own skills for the narrowing picker.
  useEffect(() => {
    if (isAdmin) {
      apiClient.listAgents({ status: 'active' }).then(setAgents).catch(() => {});
      apiClient.listRoles().then((r) => setRoles(r.data)).catch(() => {});
    }
    apiClient.getMySkills().then(setMySkills).catch(() => {});
  }, [apiClient, isAdmin]);

  function toggleScope(s: string) {
    setScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }
  function toggleSkill(id: string) {
    setSelectedSkillIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  const ownRole = useMemo(() => roles.find((r) => r.id === user?.role_id), [roles, user?.role_id]);

  async function submit() {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (identityMode === 'agent' && !agentId) {
      setError('Pick an agent to impersonate');
      return;
    }
    if (identityMode === 'role' && !roleId) {
      setError('Pick a role for this key');
      return;
    }
    if (audience === 'openai_completions' && vapiMode && !assistantId.trim()) {
      setError('Assistant ID is required for VAPI voice keys');
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      // Only persist sub-options relevant to the chosen audience. VAPI-specific fields
      // (vapi_mode marker + assistant id + session mapping) are included only in VAPI
      // mode; a generic OpenAI key carries just the filler opt-out (if turned off).
      const completions =
        audience === 'openai_completions'
          ? {
              ...(vapiMode
                ? {
                    vapi_mode: true,
                    vapi_assistant_id: assistantId.trim(),
                    ...(vapiSessionMapping ? { vapi_session_mapping: true } : {}),
                  }
                : {}),
              ...(fillerEnabled ? {} : { filler_enabled: false }),
            }
          : undefined;
      // Drop an all-empty completions bag so a vanilla generic key sends no metadata.
      const metadata =
        completions && Object.keys(completions).length > 0 ? { completions } : undefined;
      const body: CreateApiKeyBody = {
        name: name.trim(),
        audience,
        scopes,
        identity_mode: identityMode,
        ...(identityMode === 'agent' ? { agent_id: agentId } : {}),
        ...(identityMode === 'role' ? { role_id: roleId } : {}),
        ...(narrowSkills && identityMode !== 'agent' ? { skill_ids: selectedSkillIds } : {}),
        ...(metadata ? { metadata } : {}),
        // Custom auth token is a VAPI-only field — only send it in VAPI mode, so a stale
        // value can't ride along on a key whose VAPI toggle was turned back off.
        ...(audience === 'openai_completions' && vapiMode && customAuthKey.trim()
          ? { custom_auth_secret: customAuthKey.trim() }
          : {}),
      };
      const minted = await createKey(body);
      onMinted(minted);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to mint key');
      setSubmitting(false);
    }
  }

  const fieldLabel: React.CSSProperties = {
    fontSize: 11,
    color: 'var(--text-soft)',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  return (
    <div
      data-testid="mint-key-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-labelledby="mint-modal-title"
        style={{
          width: 560,
          maxWidth: '92vw',
          maxHeight: '88vh',
          overflow: 'auto',
          background: 'var(--bg)',
          borderRadius: 8,
          padding: '20px 24px',
          border: '1px solid var(--border)',
        }}
      >
        <div id="mint-modal-title" style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          New API key
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 16 }}>
          Pick a protocol, then choose what identity the key acts as. A role is always enforced.
        </div>

        {/* --- Audience (protocol) --- */}
        <div style={{ marginBottom: 16 }}>
          <div style={fieldLabel}>Protocol</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {AUDIENCE_OPTIONS.map((opt) => {
              const active = audience === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`audience-${opt.value}`}
                  onClick={() => setAudience(opt.value)}
                  title={opt.hint}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 6,
                    background: active ? 'rgba(197,107,69,0.08)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{opt.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* --- Name --- */}
        <label style={{ display: 'block', marginBottom: 16 }}>
          <div style={fieldLabel}>Name</div>
          <input
            data-testid="mint-key-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. VAPI receptionist"
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 13,
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: 'var(--bg-alt)',
              color: 'var(--text)',
              boxSizing: 'border-box',
            }}
          />
        </label>

        {/* --- Identity (acts as) --- */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...fieldLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
            Acts as
            <InfoHint label="What identity this key acts as">
              Every key runs as one identity and a role is <strong>always enforced</strong>. Capabilities
              are resolved <strong>live</strong> on each request — revoking the target or editing its role
              takes effect immediately.
            </InfoHint>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label data-testid="identity-self" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
              <input type="radio" name="identity" checked={identityMode === 'self'} onChange={() => setIdentityMode('self')} />
              Me ({user?.email ?? 'current user'})
              <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>— full user directory & memory</span>
              <InfoHint label="About impersonating yourself">
                The key acts as <strong>you</strong>, with your current role and your personal workspace
                directory/memory. Best for a personal client (e.g. your own Claude Desktop) where
                continuity across sessions is wanted.
              </InfoHint>
            </label>

            <label
              data-testid="identity-agent"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                color: isAdmin ? 'var(--text)' : 'var(--text-mute)',
                opacity: isAdmin ? 1 : 0.55,
              }}
              title={isAdmin ? '' : 'Requires manage_agents to impersonate an agent'}
            >
              <input
                type="radio"
                name="identity"
                disabled={!isAdmin}
                checked={identityMode === 'agent'}
                onChange={() => setIdentityMode('agent')}
              />
              An agent
              <InfoHint label="About impersonating an agent">
                The key runs as an autonomous <strong>agent</strong> and inherits that agent's current
                role and its own per-agent workspace. {isAdmin ? 'Pick any agent in your tenant.' : 'Requires the manage_agents capability — ask an admin.'}
              </InfoHint>
              {identityMode === 'agent' && (
                <select
                  data-testid="identity-agent-select"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  style={{ marginLeft: 6, padding: '3px 6px', fontSize: 12, borderRadius: 4 }}
                >
                  <option value="">Select agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.role.name})
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label data-testid="identity-role" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
              <input type="radio" name="identity" checked={identityMode === 'role'} onChange={() => setIdentityMode('role')} />
              No user — role only
              <span style={{ fontSize: 11, color: 'var(--text-soft)' }}>— raw connection, no directory</span>
              <InfoHint label="About role-only keys">
                A raw connection with <strong>no user</strong> and no personal directory/memory — it just
                carries a role. Good for shared/service integrations (raw MCP, OpenAI, or REST).
                {isAdmin ? ' Pick any role.' : ' Non-admins can only use their own role.'}
              </InfoHint>
            </label>
          </div>

          {identityMode === 'role' && (
            <div style={{ marginTop: 8, paddingLeft: 24 }}>
              {isAdmin ? (
                <select
                  data-testid="identity-role-select"
                  value={roleId}
                  onChange={(e) => setRoleId(e.target.value)}
                  style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, minWidth: 220 }}
                >
                  <option value="">Select role…</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                  Your role: <strong style={{ color: 'var(--text)' }}>{ownRole?.name ?? user?.role ?? 'your role'}</strong>
                  <span style={{ marginLeft: 6 }}>(non-admins can only mint keys for their own role)</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* --- OpenAI-completions sub-options (extensible) --- */}
        {audience === 'openai_completions' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ ...fieldLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
              OpenAI completions options
              <InfoHint label="About OpenAI completions options">
                The endpoint is a standard, <strong>stateless</strong> OpenAI{' '}
                <code>/chat/completions</code> by default — point any OpenAI-compatible client at it
                (LangChain, the OpenAI SDK, your own backend) and send the full message history each
                turn. The options below add platform behavior on top. More will appear here over time.
              </InfoHint>
            </div>

            {/* VAPI-mode gate: reveals the VAPI-specific bindings only when this key
                actually targets VAPI voice. A generic OpenAI client needs none of them. */}
            <label
              data-testid="vapi-mode-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}
            >
              <input
                type="checkbox"
                checked={vapiMode}
                onChange={(e) => setVapiMode(e.target.checked)}
              />
              This key is for VAPI voice
              <InfoHint label="About VAPI mode">
                Turn this on <strong>only</strong> if you're wiring this key into a VAPI assistant. It
                reveals (and requires) the VAPI assistant-id binding and the optional call→session
                mapping. For any other OpenAI-compatible client, leave it off — you'll get a plain
                stateless <code>/chat/completions</code> key with nothing VAPI-specific to fill in.
              </InfoHint>
            </label>

            {/* VAPI-only: Assistant ID binding (required) + session mapping. */}
            {vapiMode && (
              <div
                style={{
                  marginBottom: 10,
                  paddingLeft: 24,
                  borderLeft: '2px solid var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>
                    Assistant ID <span style={{ color: 'var(--kind-failure, #c0392b)' }}>*</span>
                    <InfoHint label="About the assistant ID binding">
                      VAPI sends <code>assistant.id</code> in every request body. This key is bound to
                      that assistant and <strong>every call is verified against it</strong> — a missing
                      or mismatched id is rejected (403). Combined with the key, the (key + assistant
                      id) pair is what binds a request to the right account. Find it in your VAPI
                      assistant URL.
                    </InfoHint>
                  </span>
                  <input
                    data-testid="vapi-assistant-id"
                    value={assistantId}
                    onChange={(e) => setAssistantId(e.target.value)}
                    placeholder="e.g. 297af941-858f-45ee-8ce3-b209e16442e4"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: 13,
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      background: 'var(--bg-alt)',
                      color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                </label>

                <label
                  data-testid="vapi-session-mapping-toggle"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}
                >
                  <input
                    type="checkbox"
                    checked={vapiSessionMapping}
                    onChange={(e) => setVapiSessionMapping(e.target.checked)}
                  />
                  VAPI conversation → session mapping
                  <InfoHint label="About VAPI session mapping">
                    Maps VAPI's stable call id to a persistent agent session, so a multi-turn voice
                    call <strong>resumes</strong> the same context instead of starting fresh each turn
                    — only the latest utterance is sent (faster first response, lower token use). Leave
                    off for a plain stateless OpenAI endpoint.
                  </InfoHint>
                </label>

                {/* VAPI-only: custom auth key. VAPI sends one fixed global Authorization
                    header, so this is where you paste that exact bearer. Only meaningful in
                    VAPI mode — a generic OpenAI key just uses its generated sk_oai_ secret. */}
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text)', marginBottom: 4 }}>
                    Custom auth key — sent as <code>x-api-key</code> (optional)
                    <InfoHint label="About the custom auth key">
                      VAPI sends one fixed credential for the whole org via the <code>x-api-key</code>{' '}
                      header (set under VAPI → Settings → Integrations → Custom LLM). Paste that exact
                      value here and we authenticate by it — and you can reuse the <strong>same</strong>{' '}
                      token across one key per assistant (each bound to its own Assistant ID; the
                      assistant id picks the right one). We also accept it as{' '}
                      <code>Authorization: Bearer</code>. Leave blank to generate an{' '}
                      <code>sk_oai_</code> secret instead (shown once after minting).
                    </InfoHint>
                  </span>
                  <input
                    data-testid="vapi-custom-auth-key"
                    type="password"
                    value={customAuthKey}
                    onChange={(e) => setCustomAuthKey(e.target.value)}
                    placeholder="Leave blank to auto-generate"
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: 13,
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      background: 'var(--bg-alt)',
                      color: 'var(--text)',
                      boxSizing: 'border-box',
                    }}
                  />
                </label>
              </div>
            )}

            {/* Always available: filler (helps any latency-sensitive / voice client). */}
            <label
              data-testid="filler-toggle"
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}
            >
              <input
                type="checkbox"
                checked={fillerEnabled}
                onChange={(e) => setFillerEnabled(e.target.checked)}
              />
              Instant filler (reduce dead air)
              <InfoHint label="About the filler">
                A fast model speaks an instant, message-fitting opener while the full agent works in
                the background — killing the seconds of dead air on voice calls. On by default; turn
                off to always go straight to the full agent.
              </InfoHint>
            </label>
          </div>
        )}

        {/* --- Optional skill narrowing (self/role) --- */}
        {identityMode !== 'agent' && mySkills.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)' }}>
              <input
                type="checkbox"
                data-testid="narrow-skills-toggle"
                checked={narrowSkills}
                onChange={(e) => setNarrowSkills(e.target.checked)}
              />
              Restrict this key to specific skills
              <InfoHint label="About skill narrowing">
                Limit this key to a subset of the skills <strong>you</strong> can access. You can only
                ever narrow your own access — never grant a skill you don't already have. The list
                below shows exactly the skills available to you.
              </InfoHint>
            </label>
            {narrowSkills && (
              <div
                style={{
                  marginTop: 8,
                  maxHeight: 160,
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: 6,
                }}
              >
                {mySkills.map((sk) => {
                  const checked = selectedSkillIds.includes(sk.skill_id);
                  return (
                    <label
                      key={sk.skill_id}
                      data-testid={`narrow-skill-${sk.skill_id}`}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '4px 6px', cursor: 'pointer' }}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleSkill(sk.skill_id)} />
                      <span>
                        <span style={{ fontSize: 13, color: 'var(--text)' }}>{sk.name}</span>
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--text-soft)' }}>{sk.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* --- Scopes --- */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ ...fieldLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
            Scopes
            <InfoHint label="What scopes do">
              Scopes are the specific operations this key may perform within the chosen protocol — its
              permission boundary. They <strong>narrow</strong> within the role, never widen it: a
              request must pass both the scope check and the role's capabilities.
              {audience === 'openai_completions'
                ? ' OpenAI keys have a single scope, completions:execute (call /chat/completions).'
                : audience === 'mcp'
                  ? ' e.g. mcp:read (list), mcp:execute (run tools), mcp:write (create/modify).'
                  : ' e.g. conversations:read/write, sessions:execute, profiles:read/write.'}
            </InfoHint>
          </div>
          <ScopePills audience={audience} selected={scopes} onToggle={toggleScope} />
        </div>

        {error && (
          <div data-testid="mint-key-error" style={{ color: '#c0392b', fontSize: 12, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: '1px solid var(--border)',
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--text)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            data-testid="mint-key-submit"
            onClick={() => void submit()}
            disabled={submitting}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: 0,
              borderRadius: 4,
              background: 'var(--accent)',
              color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 500,
            }}
          >
            {submitting ? 'Minting…' : 'Mint key'}
          </button>
        </div>
      </div>
    </div>
  );
}
